import {
  Box,
  Typography,
  useTheme,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Tooltip,
  LinearProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { tokens } from '../../theme';
import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ResponsiveLine } from '@nivo/line';
import fetchData from '../../helpers/fetchData';
import moment from 'moment';
import { CATEGORY_LABELS } from '../../helpers/mailingCategories';

interface CampaignDetailRow {
  campaign_id: number;
  name: string;
  send_date: string;
  recipients: number;
  unique_opens: number;
  unique_clicks: number;
  total_bounces: number;
  total_unsubscribes: number;
  open_rate: number;
  click_rate: number;
  category: string;
  revenue: number | null;
  etkp: number | null;
  revenue_attributed: boolean;
}

interface GroupedCampaignRow {
  groupKey: string;
  label: string;
  campaignCount: number;
  recipients: number;
  unique_opens: number;
  unique_clicks: number;
  total_bounces: number;
  total_unsubscribes: number;
  open_rate: number;
  click_rate: number;
  revenue: number;
  attributed_recipients: number;
  etkp: number | null;
  campaigns: CampaignDetailRow[];
}

interface CampaignTimelineData {
  date: string;
  total_campaigns: number;
  total_recipients: number;
  unique_opens: number;
  unique_clicks: number;
  total_bounces: number;
  total_unsubscribes: number;
  total_complaints: number;
  open_rate: number;
  click_rate: number;
  bounce_rate: number;
  unsubscribe_rate: number;
  complaint_rate: number;
}

interface CampaignSummaryData {
  total_campaigns: number;
  total_recipients: number;
  avg_recipients_per_campaign: number;
  total_unique_opens: number;
  total_unique_clicks: number;
  total_bounces: number;
  total_unsubscribes: number;
  avg_open_rate: number;
  avg_click_rate: number;
  avg_bounce_rate: number;
  avg_unsubscribe_rate: number;
}

interface CategoryPerformanceRow {
  category: string;
  campaigns: number;
  recipients: number;
  opens: number;
  clicks: number;
  unsubs: number;
  bounces: number;
  open_rate: number | null;
  click_rate: number | null;
  unsub_rate: number | null;
  bounce_rate: number | null;
  revenue: number | null;
  etkp: number | null;
  revenue_attributed: boolean;
}

// Explanation shown in tooltip when Revenue/ETKP = "–". Only non-attributed
// categories need a note — the three NO categories now carry their own
// revenue (via banner `_NonOpener` suffix for PLX, `is_non_opener` flag for
// webinar, `non_opener_sam` bit for VNR) so they don't appear here.
const CATEGORY_REVENUE_NOTE: Record<string, string> = {
  newsletter: `Newsletter-Revenue wird separat im Dashboard /newsletter-ad-insertions getrackt — Revenue kommt nicht aus dem Newsletter selbst, sondern aus eingebetteten Ads (Webinar-CPAs, PLX-CPAs, VNR-DOIs). Eine ETKP-Zahl hier wäre Transportmedium vs Produkt und damit irreführend.`,
  followup: `Followup-Mails (Nachfass, RNA, ANO) haben keine direkte Revenue-Attribution — die Orders, die durch einen Followup-Touchpoint assistiert werden, fließen als „Assisted-ANFOs" in die Revenue der Parent-Heat-Up/Redlink-Zeile (wenn Toggle „inkl. Followups" aktiv ist). Die Zeile hier zeigt nur Empfänger-/Engagement-Metriken des Followup-Sends selbst.`,
  heatup_legacy: `Draft-/Legacy-Heat-Ups die nicht sauber über den WebinarMailingService gesendet wurden: kein started_at, kaum oder keine Empfänger. Typisch: Doubled-creation-Artefakte oder manuelle Iterable-Anlagen die abgebrochen sind. ANFOs für diese Webinare werden den regulären Heat-Up-Sends (die tatsächlich gesendet haben) zugeordnet — daher hier leer, nicht 0.`,
  other: `"Sonstige" sind manuelle/Legacy-Kampagnen ohne Automatisierung — typischerweise pre-Nov-2025 HU/RL-Sends (vor der webinar_mailing_campaigns-Migration), [Clone]-SAMs oder Ad-hoc-Blasts. Keine Revenue-Attribution, weil sie außerhalb der getrackten Pipelines laufen. Signal: wenn hier Volumen auftaucht, prüfen ob neue Kampagnen-Typen verwendet werden.`,
};

interface CampaignStatisticsTabProps {
  startDate: string;
  endDate: string;
  aggregation: string;
}

const CampaignStatisticsTab: React.FC<CampaignStatisticsTabProps> = ({
  startDate,
  endDate,
  aggregation,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [timelineData, setTimelineData] = useState<CampaignTimelineData[]>([]);
  const [summaryData, setSummaryData] = useState<CampaignSummaryData | null>(null);
  const [detailData, setDetailData] = useState<CampaignDetailRow[]>([]);
  const [categoryPerformance, setCategoryPerformance] = useState<CategoryPerformanceRow[]>([]);
  // Toggles are URL-derived (default ON). Shareable via link; a query param
  // only appears when the toggle is flipped OFF (e.g. `?include_non_openers=0`).
  const [searchParams, setSearchParams] = useSearchParams();
  const includeNonOpeners = searchParams.get('include_non_openers') !== '0';
  const normalizeInactive = searchParams.get('normalize_inactive') !== '0';
  const includeFollowups = searchParams.get('include_followups') !== '0';

  const updateToggle = (key: string, value: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.delete(key);
    else next.set(key, '0');
    setSearchParams(next);
  };
  const setIncludeNonOpeners = (v: boolean) => updateToggle('include_non_openers', v);
  const setNormalizeInactive = (v: boolean) => updateToggle('normalize_inactive', v);
  const setIncludeFollowups = (v: boolean) => updateToggle('include_followups', v);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [hiddenSeries, setHiddenSeries] = useState<Record<string, Set<string>>>({});

  const isHidden = (chartId: string, seriesId: string) =>
    hiddenSeries[chartId]?.has(seriesId) ?? false;

  const toggleHidden = (chartId: string, seriesId: string) => {
    setHiddenSeries((prev) => {
      const next = { ...prev };
      const set = new Set(next[chartId] ?? []);
      if (set.has(seriesId)) {
        set.delete(seriesId);
      } else {
        set.add(seriesId);
      }
      next[chartId] = set;
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      try {
        const [timeline, summary, details, categoryPerf] = await Promise.all([
          fetchData<CampaignTimelineData>(
            '/api/iterable-user-statistics/campaign-timeline',
            {
              start_date: startDate,
              end_date: endDate,
              aggregation,
            },
            true,
          ),
          fetchData<CampaignSummaryData>(
            '/api/iterable-user-statistics/campaign-summary',
            {
              start_date: startDate,
              end_date: endDate,
            },
            false,
          ),
          fetchData<CampaignDetailRow>(
            '/api/iterable-user-statistics/campaign-details',
            {
              start_date: startDate,
              end_date: endDate,
              include_non_openers: includeNonOpeners ? 1 : 0,
              normalize_inactive: normalizeInactive ? 1 : 0,
              include_followups: includeFollowups ? 1 : 0,
            },
            true,
          ),
          fetchData<{ points: CategoryPerformanceRow[] }>(
            '/api/iterable-user-statistics/campaigns-performance-by-category',
            {
              start_date: startDate,
              end_date: endDate,
              include_non_openers: includeNonOpeners ? 1 : 0,
              normalize_inactive: normalizeInactive ? 1 : 0,
              include_followups: includeFollowups ? 1 : 0,
            },
            false,
          ),
        ]);

        if (!cancelled) {
          setTimelineData(timeline as CampaignTimelineData[]);
          setSummaryData(summary as CampaignSummaryData);
          setDetailData(details as CampaignDetailRow[]);
          setCategoryPerformance(
            (categoryPerf as { points: CategoryPerformanceRow[] })?.points ?? [],
          );
          setExpandedGroups(new Set());
        }
      } catch (error) {
        console.error('Error loading campaign statistics:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, aggregation, includeNonOpeners, normalizeInactive, includeFollowups]);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('de-DE').format(Math.round(num));
  };

  const nivoTheme = useMemo(
    () => ({
      axis: {
        domain: { line: { stroke: colors.grey[100] } },
        legend: { text: { fill: colors.grey[100] } },
        ticks: {
          line: { stroke: colors.grey[100], strokeWidth: 1 },
          text: { fill: colors.grey[100] },
        },
      },
      legends: { text: { fill: colors.grey[100] } },
      tooltip: { container: { color: colors.primary[500] } },
    }),
    [colors],
  );

  const buildClickableLegend = (
    allSeries: { id: string; color: string }[],
    hiddenCheck: (seriesId: string) => boolean,
    onToggle: (seriesId: string) => void,
    overrides: Record<string, any> = {},
  ) => ({
    anchor: 'bottom-right' as const,
    direction: 'column' as const,
    justify: false,
    translateX: 150,
    translateY: 0,
    itemsSpacing: 2,
    itemDirection: 'left-to-right' as const,
    itemWidth: 140,
    itemHeight: 20,
    itemOpacity: 0.75,
    symbolSize: 12,
    symbolShape: 'circle' as const,
    symbolBorderColor: 'rgba(0, 0, 0, .5)',
    effects: [
      {
        on: 'hover' as const,
        style: { itemBackground: 'rgba(0, 0, 0, .03)', itemOpacity: 1 },
      },
    ],
    ...overrides,
    data: allSeries.map((s) => ({
      id: s.id,
      label: s.id,
      color: hiddenCheck(s.id) ? colors.grey[500] : s.color,
    })),
    onClick: (datum: any) => onToggle(String(datum.id)),
  });

  const LineTooltip = ({ point }: any) => (
    <Box
      sx={{
        background: 'white',
        padding: '9px 12px',
        border: '1px solid #ccc',
        borderRadius: '4px',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
        {point.serieId}
      </Typography>
      <Typography variant="body2">
        <strong>Datum:</strong> {point.data.xFormatted}
      </Typography>
      <Typography variant="body2">
        <strong>Wert:</strong>{' '}
        {typeof point.data.y === 'number' && point.data.y < 100
          ? Number(point.data.y).toFixed(2) + ' %'
          : formatNumber(point.data.y)}
      </Typography>
    </Box>
  );

  // Chart 1: Recipients trend
  // Format chart-axis dates by aggregation. Weekly API returns "YYYY-WW"
  // which reads like a year-month (2026-09 = week 9 ≠ September 2026); prefix
  // with "KW" + swap order so it's unambiguous.
  const formatChartDate = (date: string): string => {
    if (aggregation === 'weekly' && /^\d{4}-\d{1,2}$/.test(date)) {
      const [year, week] = date.split('-');
      return `KW ${week.padStart(2, '0')}/${year}`;
    }
    return date;
  };

  const recipientsChartData = useMemo(() => {
    if (!timelineData.length) return [];
    return [
      {
        id: 'Empfänger',
        color: colors.blueAccent[500],
        data: timelineData.map((d) => ({
          x: formatChartDate(d.date),
          y: Number(d.total_recipients) || 0,
        })),
      },
    ];
  }, [timelineData, colors]);

  // Chart 2: Engagement rates - dual axis (Open Rate left, Click/Unsub Rate right)
  const engagementRatesLeftData = useMemo(() => {
    if (!timelineData.length) return [];
    return [
      {
        id: 'Open Rate',
        color: colors.greenAccent[500],
        data: timelineData.map((d) => ({
          x: formatChartDate(d.date),
          y: Number(d.open_rate) || 0,
        })),
      },
    ];
  }, [timelineData, colors]);

  const engagementRatesRightData = useMemo(() => {
    if (!timelineData.length) return [];
    return [
      {
        id: 'Click Rate',
        color: colors.blueAccent[500],
        data: timelineData.map((d) => ({
          x: formatChartDate(d.date),
          y: Number(d.click_rate) || 0,
        })),
      },
      {
        id: 'Unsubscribe Rate',
        color: colors.redAccent[500],
        data: timelineData.map((d) => ({
          x: formatChartDate(d.date),
          y: Number(d.unsubscribe_rate) || 0,
        })),
      },
    ];
  }, [timelineData, colors]);

  // Chart 3: Engagement volume - dual axis (Opens left, Clicks right)
  const engagementVolumeLeftData = useMemo(() => {
    if (!timelineData.length) return [];
    return [
      {
        id: 'Unique Opens',
        color: colors.greenAccent[500],
        data: timelineData.map((d) => ({
          x: formatChartDate(d.date),
          y: Number(d.unique_opens) || 0,
        })),
      },
    ];
  }, [timelineData, colors]);

  const engagementVolumeRightData = useMemo(() => {
    if (!timelineData.length) return [];
    return [
      {
        id: 'Unique Clicks',
        color: colors.blueAccent[500],
        data: timelineData.map((d) => ({
          x: formatChartDate(d.date),
          y: Number(d.unique_clicks) || 0,
        })),
      },
    ];
  }, [timelineData, colors]);

  // Chart 4: Deliverability (Bounce Rate, Complaint Rate).
  // Beide Raten kommen aus `iterable_campaign_metrics_cache` (aggregierte
  // Iterable-API-Daten pro Campaign) — NICHT aus dem Event-Cache. Die
  // aggregierten `total_complaints` sind seit Juli 2025 verlässlich gefüllt,
  // es gibt hier keinen 2025-10-27-Floor. (Der Floor gilt nur für die
  // Event-Level-Tabelle `iterable_campaign_events_cache`, die dieser Chart
  // nicht benutzt.)
  const deliverabilityChartData = useMemo(() => {
    if (!timelineData.length) return [];
    return [
      {
        id: 'Bounce Rate',
        color: '#ff9800',
        data: timelineData.map((d) => ({
          x: formatChartDate(d.date),
          y: Number(d.bounce_rate) || 0,
        })),
      },
      {
        id: 'Complaint Rate',
        color: colors.redAccent[500],
        data: timelineData.map((d) => ({
          x: formatChartDate(d.date),
          y: Number(d.complaint_rate) || 0,
        })),
      },
    ];
  }, [timelineData, colors]);

  const renderLineChart = (
    chartId: string,
    data: any[],
    title: string,
    yLegend: string,
    yFormat?: string,
    subtitle?: string,
  ) => {
    const visibleData = data.filter((s) => !isHidden(chartId, s.id));
    return (
      <Box sx={{ backgroundColor: colors.primary[400], mb: 3 }}>
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              padding: '0 30px',
              display: 'block',
              color: colors.grey[300],
              fontStyle: 'italic',
            }}
          >
            {subtitle}
          </Typography>
        )}
        <Box height="350px" mt="-20px">
          <ResponsiveLine
            data={visibleData}
            margin={{ top: 50, right: 160, bottom: 50, left: 70 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
            yFormat={yFormat}
            curve="monotoneX"
            tooltip={LineTooltip}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: 'Datum',
              legendOffset: 45,
              legendPosition: 'middle',
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: yLegend,
              legendOffset: -60,
              legendPosition: 'middle',
              format: yFormat === ' >-.2f' ? (v: any) => `${v}%` : undefined,
            }}
            colors={{ datum: 'color' }}
            pointSize={6}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            useMesh={true}
            legends={[
              buildClickableLegend(
                data,
                (id) => isHidden(chartId, id),
                (id) => toggleHidden(chartId, id),
              ),
            ]}
            theme={nivoTheme}
          />
        </Box>
      </Box>
    );
  };

  const renderDualAxisChart = (
    chartId: string,
    leftSeries: any[],
    rightSeries: any[],
    title: string,
    leftYLegend: string,
    rightYLegend: string,
    isPercent?: boolean,
    subtitle?: string,
  ) => {
    const visibleLeft = leftSeries.filter((s) => !isHidden(chartId, s.id));
    const visibleRight = rightSeries.filter((s) => !isHidden(chartId, s.id));
    const allLegendSeries = [...leftSeries, ...rightSeries];

    const leftMax = Math.max(...visibleLeft.flatMap((s: any) => s.data.map((d: any) => d.y)), 0);
    const rightMax = Math.max(...visibleRight.flatMap((s: any) => s.data.map((d: any) => d.y)), 0);

    if (leftMax === 0 || rightMax === 0) {
      return renderLineChart(
        chartId,
        allLegendSeries,
        title,
        leftYLegend,
        isPercent ? ' >-.2f' : undefined,
        subtitle,
      );
    }

    const scaleFactor = leftMax / rightMax;
    const rightSeriesIds = new Set(rightSeries.map((s: any) => s.id));

    const normalizedRight = visibleRight.map((series: any) => ({
      ...series,
      data: series.data.map((d: any) => ({
        ...d,
        _originalY: d.y,
        y: d.y * scaleFactor,
      })),
    }));

    const allData = [...visibleLeft, ...normalizedRight];

    const RightAxisLayer = (layerProps: any) => {
      const { innerWidth, innerHeight, yScale } = layerProps;
      const tickCount = 5;
      const step = rightMax / tickCount;
      const ticks = Array.from(
        { length: tickCount + 1 },
        (_, i) => Math.round(i * step * 100) / 100,
      );

      return (
        <g transform={`translate(${innerWidth}, 0)`}>
          <line y1={0} y2={innerHeight} stroke={colors.grey[100]} strokeWidth={1} />
          {ticks.map((value, i) => {
            const y = yScale(value * scaleFactor);
            if (isNaN(y)) return null;
            return (
              <g key={i} transform={`translate(0, ${y})`}>
                <line x2={5} stroke={colors.grey[100]} strokeWidth={1} />
                <text
                  x={10}
                  dy="0.32em"
                  textAnchor="start"
                  fill={colors.grey[100]}
                  style={{ fontSize: 11 }}
                >
                  {isPercent
                    ? `${value.toFixed(1)}%`
                    : new Intl.NumberFormat('de-DE').format(Math.round(value))}
                </text>
              </g>
            );
          })}
          <text
            transform={`translate(55, ${innerHeight / 2}) rotate(90)`}
            textAnchor="middle"
            fill={colors.grey[100]}
            style={{ fontSize: 12 }}
          >
            {rightYLegend}
          </text>
        </g>
      );
    };

    const DualTooltip = ({ point }: any) => {
      const isRight = rightSeriesIds.has(point.serieId);
      const displayValue =
        isRight && point.data._originalY !== undefined ? point.data._originalY : point.data.y;
      return (
        <Box
          sx={{
            background: 'white',
            padding: '9px 12px',
            border: '1px solid #ccc',
            borderRadius: '4px',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
            {point.serieId}
          </Typography>
          <Typography variant="body2">
            <strong>Datum:</strong> {point.data.xFormatted}
          </Typography>
          <Typography variant="body2">
            <strong>Wert:</strong>{' '}
            {isPercent
              ? Number(displayValue).toFixed(2) + ' %'
              : new Intl.NumberFormat('de-DE').format(Math.round(Number(displayValue)))}
          </Typography>
        </Box>
      );
    };

    return (
      <Box sx={{ backgroundColor: colors.primary[400], mb: 3 }}>
        <Typography variant="h5" fontWeight="600" sx={{ padding: '30px 30px 0 30px' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              padding: '0 30px',
              display: 'block',
              color: colors.grey[300],
              fontStyle: 'italic',
            }}
          >
            {subtitle}
          </Typography>
        )}
        <Box height="350px" mt="-20px">
          <ResponsiveLine
            data={allData}
            margin={{ top: 50, right: 230, bottom: 50, left: 70 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 0, max: 'auto' }}
            curve="monotoneX"
            tooltip={DualTooltip}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: 'Datum',
              legendOffset: 45,
              legendPosition: 'middle',
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: leftYLegend,
              legendOffset: -60,
              legendPosition: 'middle',
              format: isPercent ? (v: any) => `${v}%` : undefined,
            }}
            colors={{ datum: 'color' }}
            pointSize={6}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            useMesh={true}
            legends={[
              buildClickableLegend(
                allLegendSeries,
                (id) => isHidden(chartId, id),
                (id) => toggleHidden(chartId, id),
                { translateX: 220 },
              ),
            ]}
            layers={[
              'grid',
              'markers',
              'axes',
              'areas',
              'crosshair',
              'lines',
              'points',
              'slices',
              'mesh',
              'legends',
              RightAxisLayer,
            ]}
            theme={nivoTheme}
          />
        </Box>
      </Box>
    );
  };

  const computeAggregates = (rows: CampaignDetailRow[]) => {
    const recipients = rows.reduce((s, r) => s + (Number(r.recipients) || 0), 0);
    const unique_opens = rows.reduce((s, r) => s + (Number(r.unique_opens) || 0), 0);
    const unique_clicks = rows.reduce((s, r) => s + (Number(r.unique_clicks) || 0), 0);
    const total_bounces = rows.reduce((s, r) => s + (Number(r.total_bounces) || 0), 0);
    const total_unsubscribes = rows.reduce((s, r) => s + (Number(r.total_unsubscribes) || 0), 0);
    const revenue = rows.reduce(
      (s, r) => s + (r.revenue_attributed && r.revenue != null ? Number(r.revenue) : 0),
      0,
    );
    const attributed_recipients = rows.reduce(
      (s, r) => s + (r.revenue_attributed ? Number(r.recipients) || 0 : 0),
      0,
    );
    return {
      recipients,
      unique_opens,
      unique_clicks,
      total_bounces,
      total_unsubscribes,
      open_rate: recipients > 0 ? (unique_opens / recipients) * 100 : 0,
      click_rate: recipients > 0 ? (unique_clicks / recipients) * 100 : 0,
      revenue,
      attributed_recipients,
      etkp: attributed_recipients > 0 ? (revenue / attributed_recipients) * 1000 : null,
    };
  };

  const groupedData = useMemo((): GroupedCampaignRow[] => {
    if (aggregation === 'daily' || !detailData.length) return [];

    const groups = new Map<string, CampaignDetailRow[]>();
    detailData.forEach((row) => {
      const m = moment(row.send_date);
      const key = aggregation === 'weekly' ? m.format('YYYY-[W]WW') : m.format('YYYY-MM');
      const existing = groups.get(key) || [];
      existing.push(row);
      groups.set(key, existing);
    });

    const germanMonths = [
      'Januar',
      'Februar',
      'März',
      'April',
      'Mai',
      'Juni',
      'Juli',
      'August',
      'September',
      'Oktober',
      'November',
      'Dezember',
    ];

    return Array.from(groups.entries())
      .map(([groupKey, campaigns]) => {
        const agg = computeAggregates(campaigns);
        const monthLabel = (() => {
          const [year, month] = groupKey.split('-');
          return `${germanMonths[parseInt(month, 10) - 1]} ${year}`;
        })();
        const label =
          aggregation === 'weekly'
            ? `KW ${groupKey.split('W')[1]} / ${groupKey.split('-')[0]}`
            : monthLabel;
        return {
          groupKey,
          label,
          campaignCount: campaigns.length,
          ...agg,
          campaigns: campaigns.sort(
            (a, b) => new Date(b.send_date).getTime() - new Date(a.send_date).getTime(),
          ),
        };
      })
      .sort((a, b) => b.groupKey.localeCompare(a.groupKey));
  }, [detailData, aggregation]);

  const summaryRow = useMemo(() => {
    if (!detailData.length) return null;
    return computeAggregates(detailData);
  }, [detailData]);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const formatRate = (value: number) => (Number(value) || 0).toFixed(2) + ' %';

  const headerCellSx = {
    backgroundColor: '#e0e0e0 !important',
    color: 'black',
    fontWeight: 'bold',
  };

  const formatEuro = (value: number) =>
    '€ ' +
    new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  const renderRevenueCell = (row: CampaignDetailRow, bold = false, indent = false) => {
    const style: any = indent ? { pl: 5 } : {};
    if (bold) style.fontWeight = 'bold';
    if (row.revenue_attributed && row.revenue != null) {
      return (
        <TableCell align="right" sx={style}>
          {formatEuro(Number(row.revenue))}
        </TableCell>
      );
    }
    const note = CATEGORY_REVENUE_NOTE[row.category];
    return (
      <TableCell align="right" sx={style}>
        {note ? (
          <Tooltip title={note} placement="top">
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'help',
                color: colors.grey[400],
              }}
            >
              –
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        ) : (
          '–'
        )}
      </TableCell>
    );
  };

  const renderEtkpCell = (row: CampaignDetailRow, bold = false, indent = false) => {
    const style: any = indent ? { pl: 5 } : {};
    if (bold) style.fontWeight = 'bold';
    if (row.revenue_attributed && row.etkp != null) {
      return (
        <TableCell align="right" sx={style}>
          {formatEuro(Number(row.etkp))}
        </TableCell>
      );
    }
    return (
      <TableCell align="right" sx={style}>
        –
      </TableCell>
    );
  };

  const renderCampaignRow = (row: CampaignDetailRow, indent = false) => (
    <TableRow key={row.campaign_id} hover>
      <TableCell sx={indent ? { pl: 5 } : undefined}>
        {moment(row.send_date).format('DD.MM.YYYY')}
      </TableCell>
      <TableCell sx={indent ? { pl: 5 } : undefined}>{row.name}</TableCell>
      <TableCell sx={indent ? { pl: 5 } : undefined}>
        {CATEGORY_LABELS[row.category] ?? row.category}
      </TableCell>
      <TableCell align="right">{formatNumber(Number(row.recipients) || 0)}</TableCell>
      <TableCell align="right">{formatNumber(Number(row.unique_opens) || 0)}</TableCell>
      <TableCell align="right">{formatRate(Number(row.open_rate))}</TableCell>
      <TableCell align="right">{formatNumber(Number(row.unique_clicks) || 0)}</TableCell>
      <TableCell align="right">{formatRate(Number(row.click_rate))}</TableCell>
      <TableCell align="right">{formatNumber(Number(row.total_bounces) || 0)}</TableCell>
      <TableCell align="right">{formatNumber(Number(row.total_unsubscribes) || 0)}</TableCell>
      {renderRevenueCell(row)}
      {renderEtkpCell(row)}
    </TableRow>
  );

  const renderSummaryRow = () => {
    if (!summaryRow) return null;
    return (
      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
        <TableCell sx={{ fontWeight: 'bold' }}>Gesamt</TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>{detailData.length} Kampagnen</TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>—</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
          {formatNumber(summaryRow.recipients)}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
          {formatNumber(summaryRow.unique_opens)}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
          {formatRate(summaryRow.open_rate)}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
          {formatNumber(summaryRow.unique_clicks)}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
          {formatRate(summaryRow.click_rate)}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
          {formatNumber(summaryRow.total_bounces)}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
          {formatNumber(summaryRow.total_unsubscribes)}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
          {formatEuro(summaryRow.revenue)}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
          {summaryRow.etkp != null ? formatEuro(summaryRow.etkp) : '–'}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      {/* Loading indicator — slim bar at top during any fetch. Keeps existing
          content visible during re-fetches. */}
      {loading && (
        <LinearProgress
          sx={{
            mb: 2,
            height: 3,
            borderRadius: 1,
          }}
        />
      )}

      {/* Summary Cards */}
      {summaryData && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: colors.primary[400] }}>
              <CardContent>
                <Typography color={colors.grey[100]} variant="h5" fontWeight="600">
                  Kampagnen
                </Typography>
                <Typography color={colors.greenAccent[500]} variant="h3" fontWeight="bold" mt={1}>
                  {formatNumber(Number(summaryData.total_campaigns) || 0)}
                </Typography>
                <Typography color={colors.grey[300]} variant="body2">
                  im Zeitraum
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: colors.primary[400] }}>
              <CardContent>
                <Typography color={colors.grey[100]} variant="h5" fontWeight="600">
                  Gesamt-Empfänger
                </Typography>
                <Typography color={colors.greenAccent[500]} variant="h3" fontWeight="bold" mt={1}>
                  {formatNumber(Number(summaryData.total_recipients) || 0)}
                </Typography>
                <Typography color={colors.grey[300]} variant="body2">
                  ∅ {formatNumber(Number(summaryData.avg_recipients_per_campaign) || 0)} pro
                  Kampagne
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: colors.primary[400] }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography color={colors.grey[100]} variant="h5" fontWeight="600">
                    Open Rate
                  </Typography>
                  <Tooltip
                    title="Apple Mail Privacy Protection (iOS ab 15, macOS ab Monterey) lädt den Tracking-Pixel schon bei Zustellung der Mail — iOS-Empfänger zeigen damit ~100 % Open-Rate, unabhängig davon ob sie die Mail tatsächlich geöffnet haben. Die ausgewiesene Open Rate ist dadurch inflationiert. Für echtes Engagement ist die Click Rate daneben der belastbarere Indikator."
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 16,
                        color: colors.grey[300],
                        cursor: 'help',
                      }}
                    />
                  </Tooltip>
                </Box>
                <Typography color={colors.greenAccent[500]} variant="h3" fontWeight="bold" mt={1}>
                  {(Number(summaryData.avg_open_rate) || 0).toFixed(2)} %
                </Typography>
                <Typography color={colors.grey[300]} variant="body2">
                  {formatNumber(Number(summaryData.total_unique_opens) || 0)} Unique Opens
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: colors.primary[400] }}>
              <CardContent>
                <Typography color={colors.grey[100]} variant="h5" fontWeight="600">
                  Click Rate
                </Typography>
                <Typography color={colors.blueAccent[500]} variant="h3" fontWeight="bold" mt={1}>
                  {(Number(summaryData.avg_click_rate) || 0).toFixed(2)} %
                </Typography>
                <Typography color={colors.grey[300]} variant="body2">
                  {formatNumber(Number(summaryData.total_unique_clicks) || 0)} Unique Clicks
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Chart 1: Recipients Trend */}
      {renderLineChart('recipients', recipientsChartData, 'Empfänger-Entwicklung', 'Empfänger')}

      {/* Chart 2: Engagement Rates (dual axis) */}
      {renderDualAxisChart(
        'engagementRates',
        engagementRatesLeftData,
        engagementRatesRightData,
        'Engagement-Raten',
        'Open Rate (%)',
        'Click / Unsub Rate (%)',
        true,
        'Open Rate ist durch Apple Mail Privacy Protection (iOS/macOS Mail.app) inflationiert — der Tracking-Pixel wird beim Zustellen vorgeladen, Opens werden dort praktisch zu 100 %. Die Click Rate ist unberührt und der belastbarere Engagement-Indikator.',
      )}

      {/* Chart 3: Engagement Volume (dual axis) */}
      {renderDualAxisChart(
        'engagementVolume',
        engagementVolumeLeftData,
        engagementVolumeRightData,
        'Engagement-Volumen',
        'Unique Opens',
        'Unique Clicks',
        false,
        'Opens sind durch Apple MPP bei iOS-Empfängern inflationiert (Pixel lädt beim Zustellen). Click-Volumen ist unberührt.',
      )}

      {/* Chart 4: Deliverability */}
      {renderLineChart(
        'deliverability',
        deliverabilityChartData,
        'Zustellbarkeit',
        'Rate (%)',
        ' >-.2f',
        'Bounce und Complaint Rate kommen aus den aggregierten Campaign-Metriken (Iterable API), sind also für den gesamten Zeitraum vergleichbar. Per-Email Complaint-Events (nicht Bestandteil dieses Charts) werden separat erst seit 27.10.2025 erfasst.',
      )}

      {/* Performance by Send-Type Table */}
      {categoryPerformance.length > 0 && (
        <Box sx={{ backgroundColor: colors.primary[400], p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="h5" fontWeight="600">
              Performance nach Versand-Typ
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={normalizeInactive}
                    onChange={(e) => setNormalizeInactive(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Tooltip
                    title="Samstag-SAMs versenden an inaktive Nutzer, was Empfänger-Zahlen temporär aufbläht. Aktivieren ersetzt diese durch den 10-Send-Rolling-Average → fairere Rate-Vergleiche zu Wochentag-Sends. Wirkt nur auf Webinar (Heat-Up / Redlink) + PLX Direct Mailing."
                    placement="top"
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'help',
                        fontSize: '0.875rem',
                      }}
                    >
                      Inaktive normalisieren
                      <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Tooltip>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={includeFollowups}
                    onChange={(e) => setIncludeFollowups(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Tooltip
                    title={`Kontrolliert nur die Revenue-Zurechnung: bei AN werden Assisted-ANFOs (Orders mit Followup-Touchpoint aus webinar_followup_conversions) zur Heat-Up / Redlink Revenue addiert. Die separate "Webinar Followup" Zeile bleibt in beiden Fällen sichtbar — sie zeigt Empfänger/Opens/Clicks der Followup-Mails, Revenue ist immer leer (wandert in Parent-Zeile).`}
                    placement="top"
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'help',
                        fontSize: '0.875rem',
                      }}
                    >
                      inkl. Followups
                      <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Tooltip>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={includeNonOpeners}
                    onChange={(e) => setIncludeNonOpeners(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Tooltip
                    title="Toggle wirkt auf diese Tabelle + die Kampagnen-Details Tabelle unten. Non-Opener (VNR/PLX/Webinar) Rows werden komplett ein-/ausgeblendet — inkl. ihrer Empfänger, Revenue & ETKP. Andere Tabs & Charts auf dieser Seite sind nicht betroffen."
                    placement="left"
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'help',
                        fontSize: '0.875rem',
                      }}
                    >
                      Non-Opener einschließen
                      <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Tooltip>
                }
              />
            </Box>
          </Box>
          <Typography variant="body2" color={colors.grey[300]} mb={2} sx={{ lineHeight: 1.55 }}>
            <strong>Scope:</strong> Iterable-Versände an Finanztrends-Kunden (FT) im gewählten
            Zeitraum — sortiert nach Empfänger-Volumen. Raten sind volumen-gewichtet (SUM/SUM, nicht
            Ø pro Kampagne), damit kleine Kampagnen nicht überproportional gewichtet werden. Revenue
            nutzt dieselbe Live-Query-Logik wie die Evaluation-Dashboards (
            <em>/webinar-mailings?tab=evaluation</em> und <em>/plx-mailings?tab=evaluation</em>),
            aber gefiltert über das <strong>Iterable Send-Datum</strong> (<code>ic.started_at</code>
            ), nicht über das Webinar-Datum (<code>wout.start_date</code>) wie in der
            Webinar-Evaluation. PLX matcht daher Byte-für-Byte; Webinar kann leicht abweichen, wenn
            Heat-Ups im Zeitraum für Webinare <em>außerhalb</em> des Zeitraums verschickt wurden
            (oder umgekehrt).
            <br />
            <br />
            <strong>Revenue-Attribution (nicht überall 1:1 vergleichbar):</strong>
            <br />
            &nbsp;&nbsp;• <strong>VNR SAM / Non-Opener (VNR)</strong>: VNR-Affiliate-Payouts aus{' '}
            <code>mc_sams_statistics.revenue</code> pro Send, gesplittet über{' '}
            <code>non_opener_sam</code>-Flag.
            <br />
            &nbsp;&nbsp;• <strong>Webinar (Heat-Up / Redlink / Non-Opener)</strong>: CPA ×
            First-Order-per-(send, webinar, email). 1 Registrierung wird 1 Send zugeschrieben
            (first-touch), nicht allen 4 Heat-Ups der Serie — ETKP wirkt dadurch pro Send niedrig.
            NO-Webinar-Sends bekommen ihre eigene Attribution über den <code>is_non_opener</code>
            -Flag des Versands.
            <br />
            &nbsp;&nbsp;• <strong>PLX Direct Mailing / Non-Opener (PLX)</strong>: CPA × Orders via
            Banner-Regex pro <code>email_id</code>. Main vs NO werden über den{' '}
            <code>_NonOpener</code>-Suffix des Banners gesplittet — Main-Send bekommt nur Orders{' '}
            <em>ohne</em> Suffix, NO-Send nur Orders <em>mit</em> Suffix (im Banner stampen wir das
            beim Versand).
            <br />
            &nbsp;&nbsp;• <strong>Newsletter</strong>: Revenue lebt separat in{' '}
            <em>/newsletter-ad-insertions</em> — der Newsletter ist Transport- medium für
            eingebettete Ads, nicht das verkaufte Produkt.
            <br />
            &nbsp;&nbsp;• <strong>Followup / Sonstige</strong>: keine systematische Attribution
            implementiert.
            <br />
            <br />
            <strong>ETKP-Vergleich mit Vorsicht:</strong> Webinar-ETKP ist first-touch-zugewiesen
            (niedrig), PLX-ETKP ist email-kumulativ (hoch). Gleiche Formel (€/1k Empfänger),
            strukturell unterschiedliche Zähler — kein fairer head-to-head Vergleich, eher
            Orientierung pro Kategorie über Zeit.
          </Typography>
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Versand-Typ</TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Versände
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Empfänger
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Open Rate
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Click Rate
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Unsub Rate
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Bounce Rate
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Revenue
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    <Tooltip
                      title="ETKP = Revenue pro 1.000 Empfänger (Effektiver Tausend Kontakt Preis). Vergleicht die Revenue-Effizienz über verschieden große Versand-Typen."
                      placement="top"
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'help',
                        }}
                      >
                        ETKP
                        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categoryPerformance.map((row) => (
                  <TableRow key={row.category} hover>
                    <TableCell>{CATEGORY_LABELS[row.category] ?? row.category}</TableCell>
                    <TableCell align="right">{formatNumber(row.campaigns)}</TableCell>
                    <TableCell align="right">{formatNumber(row.recipients)}</TableCell>
                    <TableCell align="right">
                      {row.open_rate != null ? row.open_rate.toFixed(2) + ' %' : '–'}
                    </TableCell>
                    <TableCell align="right">
                      {row.click_rate != null ? row.click_rate.toFixed(2) + ' %' : '–'}
                    </TableCell>
                    <TableCell align="right">
                      {row.unsub_rate != null ? row.unsub_rate.toFixed(2) + ' %' : '–'}
                    </TableCell>
                    <TableCell align="right">
                      {row.bounce_rate != null ? row.bounce_rate.toFixed(2) + ' %' : '–'}
                    </TableCell>
                    <TableCell align="right">
                      {row.revenue_attributed && row.revenue != null ? (
                        '€ ' +
                        new Intl.NumberFormat('de-DE', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(row.revenue)
                      ) : CATEGORY_REVENUE_NOTE[row.category] ? (
                        <Tooltip title={CATEGORY_REVENUE_NOTE[row.category]} placement="top">
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'help',
                              color: colors.grey[400],
                            }}
                          >
                            –
                            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                          </Box>
                        </Tooltip>
                      ) : (
                        '–'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {row.revenue_attributed && row.etkp != null
                        ? '€ ' + row.etkp.toFixed(2)
                        : '–'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Campaign Detail Table */}
      {detailData.length > 0 && (
        <Box sx={{ backgroundColor: colors.primary[400], p: 3 }}>
          <Typography variant="h5" fontWeight="600" mb={1}>
            Kampagnen-Details
          </Typography>
          <Typography variant="body2" color={colors.grey[300]} mb={2} sx={{ lineHeight: 1.55 }}>
            Revenue + ETKP pro Kampagne folgen der gleichen Attribution-Logik wie die Tabelle
            &bdquo;Performance nach Versand-Typ&ldquo; oben. Zellen mit &bdquo;&ndash;&ldquo; haben
            einen Info-Tooltip, der erklärt warum (z.B. Newsletter-Revenue lebt in{' '}
            <em>/newsletter-ad-insertions</em>, PLX-NO rollt in Main-PLX).{' '}
            <strong>Gesamt-Revenue matcht nicht 1:1</strong> mit der Performance-per-Typ-Tabelle —
            hier werden Kampagnen mit <code>[Clone]</code> im Namen gefiltert, dort nicht.
          </Typography>
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {aggregation !== 'daily' && <TableCell sx={{ ...headerCellSx, width: 40 }} />}
                  <TableCell sx={headerCellSx}>Datum</TableCell>
                  <TableCell sx={headerCellSx}>Name</TableCell>
                  <TableCell sx={headerCellSx}>Typ</TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Empfänger
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Unique Opens
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Open Rate
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Unique Clicks
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Click Rate
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Bounces
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Abmeldungen
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Revenue
                  </TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    <Tooltip
                      title="ETKP = Revenue pro 1.000 Empfänger (nur attribuierte Kampagnen). Nicht-attribuierte Zeilen (Newsletter, Followup, NO-PLX, NO-Webinar, Sonstige) zählen nicht in den Nenner."
                      placement="top"
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'help',
                        }}
                      >
                        ETKP
                        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aggregation === 'daily' ? (
                  <>
                    {detailData.map((row) => renderCampaignRow(row))}
                    {renderSummaryRow()}
                  </>
                ) : (
                  <>
                    {groupedData.map((group) => (
                      <React.Fragment key={group.groupKey}>
                        <TableRow
                          hover
                          onClick={() => toggleGroup(group.groupKey)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: '#fafafa',
                          }}
                        >
                          <TableCell sx={{ width: 40, p: 0, pl: 1 }}>
                            <IconButton size="small">
                              {expandedGroups.has(group.groupKey) ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{group.label}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {group.campaignCount} Kampagnen
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>—</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatNumber(group.recipients)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatNumber(group.unique_opens)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatRate(group.open_rate)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatNumber(group.unique_clicks)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatRate(group.click_rate)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatNumber(group.total_bounces)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatNumber(group.total_unsubscribes)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatEuro(group.revenue)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {group.etkp != null ? formatEuro(group.etkp) : '–'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            colSpan={13}
                            sx={{
                              p: 0,
                              borderBottom: expandedGroups.has(group.groupKey) ? undefined : 'none',
                            }}
                          >
                            <Collapse
                              in={expandedGroups.has(group.groupKey)}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Table size="small">
                                <TableBody>
                                  {group.campaigns.map((campaign) => (
                                    <TableRow key={campaign.campaign_id} hover>
                                      <TableCell sx={{ pl: 7, width: 40 }} />
                                      <TableCell sx={{ pl: 1 }}>
                                        {moment(campaign.send_date).format('DD.MM.YYYY')}
                                      </TableCell>
                                      <TableCell>{campaign.name}</TableCell>
                                      <TableCell>
                                        {CATEGORY_LABELS[campaign.category] ?? campaign.category}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(Number(campaign.recipients) || 0)}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(Number(campaign.unique_opens) || 0)}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatRate(Number(campaign.open_rate))}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(Number(campaign.unique_clicks) || 0)}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatRate(Number(campaign.click_rate))}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(Number(campaign.total_bounces) || 0)}
                                      </TableCell>
                                      <TableCell align="right">
                                        {formatNumber(Number(campaign.total_unsubscribes) || 0)}
                                      </TableCell>
                                      {renderRevenueCell(campaign)}
                                      {renderEtkpCell(campaign)}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                    {/* Summary row for grouped mode */}
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell />
                      <TableCell sx={{ fontWeight: 'bold' }}>Gesamt</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {detailData.length} Kampagnen
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>—</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {summaryRow && formatNumber(summaryRow.recipients)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {summaryRow && formatNumber(summaryRow.unique_opens)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {summaryRow && formatRate(summaryRow.open_rate)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {summaryRow && formatNumber(summaryRow.unique_clicks)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {summaryRow && formatRate(summaryRow.click_rate)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {summaryRow && formatNumber(summaryRow.total_bounces)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {summaryRow && formatNumber(summaryRow.total_unsubscribes)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {summaryRow && formatEuro(summaryRow.revenue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {summaryRow && summaryRow.etkp != null ? formatEuro(summaryRow.etkp) : '–'}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default CampaignStatisticsTab;
